// The data layer (plan §4.5): every call goes through the generated @radish/api-client. Queries are TanStack Query
// reads keyed `[profile, locationId, name, input]`, so switching location never shows another location's cache;
// commands carry a fresh UUIDv7 `commandId` per attempt the person makes (a retry of the same attempt replays).
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { abortingFetch, createAuthClient, createClient, type AuthClient, type Client } from '@radish/api-client';
import type { BackofficeEnvironment } from '../env/environment.ts';
import { bearerToken, bindAuthClient, refreshSession, signOut } from '../session/session.ts';
import { uuidv7 } from '../lib/ids.ts';
import { messageFor } from './errors.ts';

/** A failed call as the UI sees it: the typed error code, its details and a sentence to show. */
export class CallFailure extends Error {
  constructor(
    readonly code: string,
    readonly details: unknown,
    readonly status: number,
  ) {
    super(messageFor(code, details));
  }
}

interface Api {
  readonly client: Client;
  /** Sign-in, refresh, switch and the rest of the session transport (`/v1/auth/*`). */
  readonly auth: AuthClient;
  readonly environment: BackofficeEnvironment;
  /** A client that sends its own bearer token instead of the session's (the merchant's review link). */
  readonly clientFor: (bearer: string) => Client;
  /**
   * The session's client with every request it sends aborted when `signal` aborts (a report query's own signal: a page
   * left or refiltered stops the request already in flight, B1384). A client handed in (tests) is used as it is.
   */
  readonly signalled: (signal: AbortSignal) => Client;
}

const ApiContext = createContext<Api | null>(null);

export function ApiProvider({ environment, client, auth, clientFor, children }: {
  readonly environment: BackofficeEnvironment; readonly client?: Client; readonly auth?: AuthClient; readonly clientFor?: (bearer: string) => Client;
  readonly children: ReactNode;
}) {
  const value = useMemo<Api>(() => {
    const session = {
      baseUrl: environment.apiBase,
      profile: environment.profile,
      headers: (): Record<string, string> => {
        const token = bearerToken();
        return token === null ? {} : { authorization: `Bearer ${token}` };
      },
    };
    const sessionClient = client ?? createClient(session);
    return {
      environment,
      auth: auth ?? createAuthClient({ baseUrl: environment.apiBase }),
      client: sessionClient,
      clientFor: clientFor ?? ((bearer: string) => createClient({
        baseUrl: environment.apiBase, profile: environment.profile, headers: (): Record<string, string> => ({ authorization: `Bearer ${bearer}` }),
      })),
      signalled: (signal: AbortSignal) => (client !== undefined ? client : createClient({ ...session, fetch: abortingFetch(signal) })),
    };
  }, [environment, client, auth, clientFor]);
  bindAuthClient(value.auth);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApi(): Api {
  const api = useContext(ApiContext);
  if (api === null) throw new Error('useApi needs an <ApiProvider>');
  return api;
}

export type CallResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly status: number; readonly error: { readonly code: string; readonly details?: unknown } };

/**
 * Unwraps a client result for TanStack Query: data, or a thrown CallFailure. An access token the server no longer
 * accepts is refreshed once from the cookie and the call retried. The session ends, as expired, only when the server
 * rejects the cookie (refreshSession) or refuses the call again with a fresh token; a refresh that couldn't reach the
 * server keeps the session, and the call fails as it would on any network error.
 */
export async function unwrap<T>(call: () => Promise<CallResult<T>>): Promise<T> {
  let result = await call();
  let refreshed = false;
  if (!result.ok && result.error.code === 'unauthenticated' && bearerToken() !== null && (await refreshSession())) {
    result = await call();
    refreshed = true;
  }
  if (result.ok) return result.data;
  if (refreshed && result.error.code === 'unauthenticated') signOut('expired');
  throw new CallFailure(result.error.code, result.error.details, result.status);
}

/** A location-scoped read: the key starts with the profile and the location (Q-GLB-02 FIX). */
export function useLocationQuery<T>(locationId: string, name: string, input: unknown, read: (client: Client) => Promise<CallResult<T>>, enabled = true) {
  const { client, environment } = useApi();
  const queryKey: QueryKey = [environment.profile, locationId, name, input];
  return useQuery<T, CallFailure>({ queryKey, queryFn: () => unwrap(() => read(client)), enabled });
}

/**
 * A command bound to the client. `invalidates` names the queries whose data the command changes (their name, under
 * this location), refetched on success.
 */
export function useCommand<I, T>(
  locationId: string,
  send: (client: Client, input: I, call: { readonly commandId: string }) => Promise<CallResult<T>>,
  invalidates: readonly string[],
) {
  const { client, environment } = useApi();
  const queryClient = useQueryClient();
  return useMutation<T, CallFailure, I>({
    mutationFn: (input) => {
      const commandId = uuidv7();
      return unwrap(() => send(client, input, { commandId }));
    },
    onSuccess: async () => {
      await Promise.all(invalidates.map((name) => queryClient.invalidateQueries({ queryKey: [environment.profile, locationId, name] })));
    },
  });
}
